



















import { VueCustom, CustomVueConstructor } from 'vue-custom';
import { WorkspaceElement, Element } from 'types';
import { onAnimationEnd, reappend, delay } from 'utils';
import { mapGetters } from 'stores';
import { DRAG_WORKSPACE } from 'constants/drag';
import * as MIX_RESULT from 'constants/mix-result';
import iconUrl from 'helpers/icons';
import Draggable from 'models/draggable';

type ComponentCustom = {
  draggable: Draggable | null,
  $refs: {
    element: HTMLDivElement,
    icon: HTMLImageElement,
  },
};
export default (VueCustom as CustomVueConstructor<ComponentCustom>).extend({
  name: 'workspace-box',
  props: {
    data: Object as PropType<WorkspaceElement>,
  },
  data: () => ({
    isDragging: false,
    hover: false,
    isClone: false,
    cloned: false,
  }),
  computed: {
    ...mapGetters([
      'elementById',
      'isArtModeEnabled',
      'draggingOverId',
      'mixFather',
      'mixStatus',
      'elementIsExhausted',
    ]),
    id(): WorkspaceElement['id'] {
      return this.data.id;
    },
    elementId(): WorkspaceElement['elementId'] {
      return this.data.elementId;
    },
    position(): WorkspaceElement['position'] {
      return this.data.position;
    },
    element(): Element | null {
      return this.elementById(this.elementId);
    },
    name(): string {
      if (!this.element) return '';

      return this.element.name;
    },
    isBelow(): boolean {
      return this.draggingOverId === this.id && !this.isDragging;
    },
    hasFailed(): boolean {
      const mixFather = this.mixFather;
      return this.mixStatus === MIX_RESULT.FAIL
          && !!mixFather && mixFather.id === this.id;
    },
    hasAlreadyMixed(): boolean {
      const mixFather = this.mixFather;
      return this.mixStatus === MIX_RESULT.MIXED
          && !!mixFather && mixFather.id === this.id;
    },
    iconUrl(): string {
      return iconUrl(this.elementId);
    },
    style(): Partial<CSSStyleDeclaration> {
      const { x = -500, y = -500 } = this.position;
      return {
        position: 'absolute',
        top: `${ y }px`,
        left: `${ x }px`,
      };
    },
    isExhausted(): boolean {
      return !!this.elementIsExhausted(this.elementId);
    },
    classes(): Record<string, boolean> {
      return {
        'is-dragging': this.isDragging,
        'js-hover': this.hover,

        'js-pulse': this.isBelow,
        'js-mix-fail': !this.isDragging && this.hasFailed,
        'js-mix-mixed': !this.isDragging && this.hasAlreadyMixed,
        'js-cloned': !this.isDragging && this.cloned,

        'js-clone': this.isClone,

        'js-to-remove': !!this.element && this.isExhausted && !this.isArtModeEnabled,
      };
    },
  },
  mounted() {
    this.initDraggable();
  },
  beforeDestroy() {
    if (this.draggable) {
      this.draggable.destroy();
      this.draggable = null;
    }
  },
  methods: {
    initDraggable() {
      this.draggable = new Draggable(this.$refs.element, {
        id: this.id,
        handle: [this.$refs.icon],
        type: DRAG_WORKSPACE,
        position: { ...this.position },
      });

      this.draggable.onDragStart = () => this.onDragStart();
      this.draggable.onDragEnd = () => this.onDragEnd();
    },
    onDragStart() {
      this.isDragging = true;
      this.isClone = false;
      // reappend to move to last position in DOM
      reappend(this.$refs.element);

      window.setTimeout(() => {
        this.hover = false;
      }, 150);
    },
    onDragEnd() {
      this.isDragging = false;
    },

    onMouseEnter() {
      if (this.isDragging || this.isClone) return;

      this.hover = true;
    },
    onMouseLeave() {
      if (this.isDragging) return;

      this.hover = false;
      this.isClone = false;
    },
    onDoubleTap() {
      this.$store.dispatch('workspaceElementClone', this.id);

      this.$nextTick(async () => {
        this.cloned = true;
        if (this.$refs.element) {
          await onAnimationEnd(this.$refs.element);
        } else {
          await delay(50);
        }
        this.cloned = false;
      });
    },
    onClone() {
      this.$nextTick(() => {
        this.isClone = true;
      });
    },

    updateDraggablePosition() {
      if (!this.draggable) return;

      this.draggable.setPosition({ ...this.position });
    },
  },
});
